import React, { useContext } from 'react';
import { FontSet, FontLabel, ProFontSets, type FontSetId } from '@stackje/common/fontSet';
import { ProBadge } from './ProBadge';
import { AuthContext } from '@/context/AuthContext';
import { useSubscriptionQuery } from '@/hooks/useSubscriptionQuery';
import { useNavigate } from '@tanstack/react-router';

export interface FontSetItem {
  id: FontSetId;
  name: string;
  heading: string;
  subheading: string;
  body: string;
}

interface FontSetSelectorProps {
  selectedFontSetId: FontSetId | null;
  onFontSetSelect: (id: FontSetId) => void;
  title?: string;
  hideProFonts?: boolean; // If true, hide pro fonts entirely; if false, show them but lock them for non-pro users
}

const FontSetLabels: Record<FontSetId, string> = {
  modern: "Modern",
  retro: "Retro", 
  elegant: "Elegant",
  friendly: "Friendly",
  creative: "Creative",
  clarity: "Clarity",
  casual: "Casual",
  "boho-chic": "Boho Chic",
  "retro-vibes": "Retro Vibes",
  "handcraft-studio": "Handcraft Studio",
  "beach-house": "Beach House",
  "coffee-shop": "Coffee Shop",
  "art-studio": "Art Studio",
  "garden-party": "Garden Party",
  "indie-maker": "Indie Maker",
  "creative-freelancer": "Creative Freelancer",
  "cozy-cafe": "Cozy Cafe",
  "startup-hustle": "Startup Hustle",
  "mystic-moon": "Mystic Moon",
  "urban-street": "Urban Street",
  "vintage-typewriter": "Vintage Typewriter",
  "digital-nomad": "Digital Nomad",
  "artisan-craft": "Artisan Craft",
  "festival-vibes": "Festival Vibes",
  "nordic-minimal": "Nordic Minimal",
  "tropical-paradise": "Tropical Paradise",
  "tech-startup": "Tech Startup",
};

// Helper function to extract font family name from class
const getFontFamilyName = (fontClass: string): string => {
  const fontName = fontClass.replace(/\s+font-bold$/, '').trim();
  return FontLabel[fontName] || fontName;
};

export const FontSetSelector: React.FC<FontSetSelectorProps> = ({
  selectedFontSetId,
  onFontSetSelect,
  title,
  hideProFonts = false,
}) => {
  const authContext = useContext(AuthContext);
  const { data: subscription } = useSubscriptionQuery(authContext?.user?.id);
  const isPro = subscription?.plan_type === 'pro';
  const navigate = useNavigate();
  let fontSets: FontSetItem[] = Object.entries(FontSet).map(([key, fonts]) => ({
    id: key as FontSetId,
    name: FontSetLabels[key as FontSetId],
    heading: fonts.heading,
    subheading: fonts.subheading,
    body: fonts.body,
  }));

  // Filter out pro fonts if hideProFonts is true and user is not pro
  if (hideProFonts && !isPro) {
    fontSets = fontSets.filter(fontSet => !ProFontSets.includes(fontSet.id));
  }

  return (
    <div>
      <h2 className="text-sm font-medium mb-3">{title}</h2>
      <div className="grid grid-cols-2 gap-4">
        {fontSets.map((fontSet) => {
          const isProFont = ProFontSets.includes(fontSet.id);
          const isLocked = isProFont && !isPro;
          
          const handleClick = () => {
            if (isLocked) {
              navigate({ to: '/subscription' });
            } else {
              onFontSetSelect(fontSet.id);
            }
          };

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          };
          
          return (
            <div
              key={fontSet.id}
              className={`p-4 border rounded-lg transition-all h-36 flex flex-col relative cursor-pointer ${
                selectedFontSetId === fontSet.id
                  ? 'border-black ring-2 ring-black shadow-md'
                  : 'border-gray-300 hover:border-gray-400'
              } ${
                isLocked ? 'opacity-60' : ''
              }`}
              onClick={handleClick}
              role="radio"
              aria-checked={selectedFontSetId === fontSet.id}
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
            <div className="space-y-1 flex-1 min-h-0">
              <div className={`text-lg font-${fontSet.heading} truncate`}>
                {getFontFamilyName(fontSet.heading)}
              </div>
              <div className={`text-sm tracking-wide font-${fontSet.subheading} truncate`}>
                {getFontFamilyName(fontSet.subheading)}
              </div>
              <div className={`text-xs font-${fontSet.body} truncate`}>
                {getFontFamilyName(fontSet.body)}
              </div>
            </div>
            {isProFont && (
              <div className="absolute top-2 right-2">
                <ProBadge isPro={isPro} className="text-xs px-1.5 py-0.5" />
              </div>
            )}
            <div className="flex items-center justify-center mt-2">
              <p className="text-center text-sm font-medium truncate">{fontSet.name}</p>
            </div>
            {isLocked && (
              <div className="absolute inset-0 bg-black/5 rounded-lg" />
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}; 